if (!customElements.get('kinderreigen-sticky-cart')) {
  customElements.define('kinderreigen-sticky-cart', class extends HTMLElement {
    connectedCallback() {
      this.pastButton = false;
      this.product = this.closest('product-info');
      this.productForm = this.product?.querySelector('product-form');
      this.sourceButton = this.productForm?.querySelector('[type="submit"]');
      this.sourcePrice = this.product?.querySelector(`#price-${this.product.dataset.section}`);
      if (!this.sourceButton || !this.sourcePrice || !window.IntersectionObserver) return;

      this.button = this.querySelector('[data-sticky-submit]');
      this.price = this.querySelector('[data-sticky-price]');
      this.originalTabindex = this.sourceButton.getAttribute('tabindex');
      this.originalAriaHidden = this.sourceButton.getAttribute('aria-hidden');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.scheduleSync = () => queueMicrotask(() => this.isConnected && this.sync());
      this.product.addEventListener('product-form:state-change', this.scheduleSync, options);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId === this.product.dataset.section) this.scheduleSync();
      });
      this.mobile.addEventListener('change', this.scheduleSync, options);

      // Includes keyboard dismissal and native <details>; overlay visibility itself is CSS-driven.
      ['click', 'keyup', 'toggle', 'focusin', 'focusout'].forEach((type) => {
        document.addEventListener(type, this.scheduleSync, { ...options, capture: true });
      });
      this.button.addEventListener('click', () => {
        // Use the original submitter: Dawn owns variant ID, quantity, properties and cart feedback.
        if (this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true') return;
        this.sourceButton.click();
        this.sync();
      }, options);
      this.observeButton();
      this.resizeObserver = new ResizeObserver((entries) => {
        if (entries.some(({ target }) => target === this.product)) this.observeButton();
        const height = this.getBoundingClientRect().height;
        if (height) document.body.style.setProperty('--kinderreigen-sticky-height', `${height}px`);
      });
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.product);
      const variantSelectors = this.product.querySelector('variant-selects');
      if (variantSelectors) {
        this.variantObserver = new IntersectionObserver(this.scheduleSync);
        this.variantObserver.observe(variantSelectors);
      }
      this.sync();
    }

    observeButton() {
      this.observer?.disconnect();
      // Include the page below the viewport: jumping from above the form to below it
      // must also cross an intersection boundary (e.g. an anchor link or End key).
      this.observer = new IntersectionObserver(([entry]) => {
        this.pastButton = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.sync();
      }, { rootMargin: `0px 0px ${document.documentElement.scrollHeight}px 0px` });
      this.observer.observe(this.sourceButton);
    }

    sync() {
      // Run after Dawn's synchronous updates, including setUnavailable (which emits no variantChange).
      const priceHtml = this.sourcePrice.classList.contains('hidden') ? '' : this.sourcePrice.innerHTML;
      if (this.price.innerHTML !== priceHtml) this.price.innerHTML = priceHtml;
      const label = this.productForm.submitButtonText.textContent;
      if (this.button.textContent !== label) this.button.textContent = label;
      this.button.disabled = this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true';
      this.button.setAttribute('aria-busy', String(this.sourceButton.classList.contains('loading')));
      const error = this.productForm.querySelector('.product-form__error-message-wrapper:not([hidden])');
      const focusedPicker = this.product.contains(document.activeElement)
        ? document.activeElement.closest('variant-selects')?.getBoundingClientRect()
        : null;
      const selectingVariant = focusedPicker && focusedPicker.bottom > 0 && focusedPicker.top < window.innerHeight;
      const show = this.mobile.matches && this.pastButton && !error && !selectingVariant;
      this.hidden = !show;
      const visible = show && getComputedStyle(this).display !== 'none';
      if (visible) {
        if (document.activeElement === this.sourceButton) this.button.focus({ preventScroll: true });
        this.sourceButton.setAttribute('aria-hidden', 'true');
        this.sourceButton.setAttribute('tabindex', '-1');
      } else {
        this.restoreSourceAccessibility();
      }
      if (!show && this.contains(document.activeElement)) {
        this.sourceButton.focus();
      }
      // Keep Dawn's error beside the original form in view rather than hiding it below a fixed bar.
      if (error && this.pastButton && this.mobile.matches) {
        this.productForm.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    }

    restoreSourceAccessibility() {
      for (const [name, value] of [['tabindex', this.originalTabindex], ['aria-hidden', this.originalAriaHidden]]) {
        if (value === null) this.sourceButton.removeAttribute(name);
        else this.sourceButton.setAttribute(name, value);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.variantObserver?.disconnect();
      if (this.sourceButton && this.mobile) this.restoreSourceAccessibility();
      document.body.style.removeProperty('--kinderreigen-sticky-height');
    }
  });
}
